// src/App.tsx

import React, { StrictMode, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { detect } from "detect-browser";
import { MainAppReducer } from "Types";
import { CustomCircularProgress } from "Components/StyledComponents";
// import Welcome from './Containers/Welcome/Welcome';
import Welcome from "Containers/TrialWelcome/Welcome";
import Tutorial from "./Containers/Tutorial/Tutorial";
import Introduction from "./Containers/Introduction/Introduction";
import Lesson from "./Containers/Lesson/Lesson";
import Signin from "./Containers/SignIn";
import Signup from "./Containers/SignUp";
import Plan from "./Containers/Plan/Plan";
import Roadmap from "./Containers/Roadmap/Roadmap";
import Repertoire from "./Containers/Repertoire/Repertoire";
import RepertoirePlay from "./Containers/Repertoire/RepertoirePlay";
import Help from "./Containers/Help/Help";
import Settings from "./Containers/Settings/Settings";
import ConnectMidi from "Containers/TrialWelcome/ConnectMidi";
import SoundOutput from "Containers/TrialWelcome/SoundOutput";
import TrialWelcome from "./Containers/TrialWelcome/TrialWelcome";
import ErrorPage from "./Containers/ErrorPage/ErrorPage";
import Verification from "./Containers/Verification";
import Dashboard from "Containers/Dashboard/Dashboard";
import { useState } from "react";
import ProtectedRoute from "Containers/ProtectedRoute";
// import { Amplify, Auth, Hub } from "aws-amplify";
// import { initializeMidiInput } from 'Utils/MidiInput';
import { detectDevice, midiPitchToFrequency } from "Utils";
import { AwsRum } from "aws-rum-web";
import logger from 'Utils/Logger';
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { App as CapApp } from '@capacitor/app';
import { AuthProvider, hasAuthParams } from "react-oidc-context";
import AuthWrapperProvider, { oidcManager, useAuthWrapperContext } from "Contexts/AuthWrapperContext";
// import { useAuth } from "AuthContext";
import { SALAMANDER_B64 } from 'assets/audio/salamander.base64';
import { decodeBase64ToAudioBuffer } from 'Utils/audioBuffers';
import { Browser } from "@capacitor/browser";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom"; //Switch
import {
  DeviceCompatibilityBlockingScreen,
} from "Containers/DeviceCompatibilityBlockingScreen";
import {
  isSafariBrowser,
  isMobileBrowser,
  isCompatibleAndroid,
} from "Utils/UserAgent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { RepertoirePlayProvider } from "Contexts/RepertoirePlayContext";

//import {requestClientDataRequest} from './Actions/app'
import * as appActions from "./Actions/app";
import * as repertoireActions from "./Actions/repertoire";
import * as eventActions from "./Actions/events";
import * as lessonActions from 'Actions/lesson';
import * as Tone from "tone";
import { MIN_NUM_PHRASES, TONE_SAMPLES } from "Utils/Constants";
import { useNavigate } from "react-router-dom";
import { MidiProvider } from "Contexts/MidiContext";
import { ALLOWED_PATHS_ANY_PLATFORM } from "Utils/Constants";
import { Store } from "redux";
import ReactGA from "react-ga4";
import { AuthReducer } from "Types/AuthTypes";
import { EventReducer } from "Types/EventTypes";
import { NoteType } from "opensheetmusicdisplay";
import NoteLength from "Components/Exercises/NoteLengthsByType";
import { isCapacitorNative } from "Utils/Constants";
import { setupIAP } from "iap/initPurchases";
import { awsRum } from "Utils/AwsRum";
import MaintenancePage from "Containers/Maintenance";
import { AppDispatch } from 'Reducers';
import { useMediaQueries } from "Hooks/useMediaQueries";
import { red } from "@mui/material/colors";
import OfflineModal from 'Components/OfflineModal';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { eventQueue } from "Models/EventQueue";

const browser = detect();

const region = process.env.REACT_APP_COGNITO_AWS_REGION;
const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
// const cognitoGuestRoleArn = process.env.REACT_APP_COGNITO_GUEST_ROLE_ARN;
// let applicationVersion = process.env.REACT_APP_APPLICATION_VERSION;
const oauthDomain = process.env.REACT_APP_OAUTH_REDIRECT_URL
const ganayticsMeasurementId = process.env.REACT_APP_G_ANALYTICS_MEASUREMENT_ID
const eventIntervalSeconds = process.env.REACT_APP_EVENT_INTERVAL_SECONDS;
if (!eventIntervalSeconds) {
  throw Error("REACT_APP_EVENT_INTERVAL_SECONDS is not set")
}
const eventIntervalSecondsInt = parseInt(eventIntervalSeconds)

const LANDSCAPE_PATHS = ['/roadmap', '/tutorial', '/lesson', '/settings'];

if (process.env.REACT_APP_NODE_ENV == "prd" && ganayticsMeasurementId) {
  ReactGA.initialize(ganayticsMeasurementId);
}

const IS_NATIVE = Capacitor.isNativePlatform() // Must be defined outside of component.


function AudioUnlocker() {
  const armedRef = useRef(false);
  const unlockedRef = useRef(false);

  useEffect(() => {
    const removeHandlers: Array<() => void> = [];

    const unlockFromGesture = async () => {
      if (unlockedRef.current) return;

      try {
        // Start/resume MUST be within the gesture handler.
        await Tone.start();
        await Tone.getContext().resume();

        let raw = Tone.getContext().rawContext;
        if (raw.state === "closed") {
          // if WKWebView nuked the context, recreate Tone context
          const newCtx = new Tone.Context({ latencyHint: "interactive" });
          Tone.setContext(newCtx);
          await Tone.start();
          await Tone.getContext().resume();
          raw = Tone.getContext().rawContext;
        }

        if (raw.state !== "running") {
          await raw.resume();
        }

        // If we resumed from sleep/route-change, rebuild sampler on this new context
        // const needsRebuild = !!(window as any).__audioNeedsRebuild;
        // const ensure = (window as any).__ensureSamplerReady;
        // if (typeof ensure === "function") {
        //   await ensure(needsRebuild); // force rebuild when dirty
        // }
        // (window as any).__audioNeedsRebuild = false;
        const needsRebuild = !!(window as any).__audioNeedsRebuild;
        const ensure = (window as any).__ensureSamplerReady;

        if (typeof ensure === "function") {
          await ensure(needsRebuild); // <- this now actually rebuilds the sampler+buffers
        }
        (window as any).__audioNeedsRebuild = false;

        // kill any stuck voices
        try { (window as any).__museflowSampler?.releaseAll?.(); } catch {}

        unlockedRef.current = true;
        (window as any).__audioUnlocked = true;

        removeHandlers.forEach(fn => fn());
      } catch (e) {
        console.warn("[AudioUnlock] failed", e);
      }
    };

    const armGesture = () => {
      if (armedRef.current || unlockedRef.current) return;
      armedRef.current = true;

      const opts: AddEventListenerOptions = { capture: true, passive: false };
      const handler = () => { unlockFromGesture(); };

      window.addEventListener("pointerdown", handler, opts);
      window.addEventListener("touchstart", handler, opts);
      window.addEventListener("keydown", handler, { capture: true });

      removeHandlers.push(() => window.removeEventListener("pointerdown", handler, opts));
      removeHandlers.push(() => window.removeEventListener("touchstart", handler, opts));
      removeHandlers.push(() => window.removeEventListener("keydown", handler, { capture: true } as any));
    };

    // const onActive = () => {
    //   // mark audio dirty; force sampler rebuild on next gesture
    //   (window as any).__audioNeedsRebuild = true;

    //   unlockedRef.current = false;
    //   (window as any).__audioUnlocked = false;

    //   armedRef.current = false;
    //   armGesture();
    // };
    const onActive = () => {
      (window as any).__audioNeedsRebuild = true;
      (window as any).__audioUnlocked = false;
      window.dispatchEvent(new CustomEvent("AudioReady", { detail: { ready: false } }));

      unlockedRef.current = false;
      armedRef.current = false;
      armGesture();
    };

    armGesture();

    const vis = () => { if (!document.hidden) onActive(); };
    document.addEventListener("visibilitychange", vis);

    let h1: any, h2: any;
    CapApp.addListener("appStateChange", ({ isActive }) => { if (isActive) onActive(); }).then(h => (h1 = h));
    CapApp.addListener("resume", onActive).then(h => (h2 = h));

    return () => {
      document.removeEventListener("visibilitychange", vis);
      h1?.remove?.();
      h2?.remove?.();
      removeHandlers.forEach(fn => fn());
    };
  }, []);

  return null;
}

function myFunc(myvar: string) {

}

interface AppProps {
  // store: any; //Store<RootState>;
  // store: Store<RootState>;
}
// function App({ store }) {
const App: React.FC<AppProps> = () => {
  // If the user hits refresh, on /lesson for example, we want to reload page with all of their current data
  // So we will want at top level here to set state from local store. (and then do whatever follow-up queries are necessary if there are any right off the bat)
  // State data should be: Current phrase (like s3 url), current lesson, current Tempo, ...
  const auth = useAuthWrapperContext()
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const { currentAuthToken } = auth
  // const { user, currentAuthToken } = useAuthWrapperContext();
  // const samplerRef = React.useRef<Tone.Sampler | null>(null);
  // const [samplerReady, setSamplerReady] = React.useState(false);
  // const sampler = React.useMemo(
  //   () => new Tone.Sampler({ urls: {}, release: 1 }).toDestination(),
  //   []
  // );
  
  // // Expose sampler to window for AudioUnlocker to access
  // React.useEffect(() => {
  //   (window as any).__museflowSampler = sampler;
  //   return () => {
  //     delete (window as any).__museflowSampler;
  //   };
  // }, [sampler]);

  const [sampler, setSampler] = React.useState(() =>
    new Tone.Sampler({ urls: {}, release: 1 }).toDestination()
  );

  const samplerRef = React.useRef<Tone.Sampler>(sampler);
    React.useEffect(() => { samplerRef.current = sampler; }, [sampler]);

    const loadingRef = React.useRef<Promise<void> | null>(null);
    const samplesLoadedRef = React.useRef(false);

    React.useEffect(() => {
    (window as any).__ensureSamplerReady = async (forceRebuild = false) => {
      if (loadingRef.current) return loadingRef.current;

      loadingRef.current = (async () => {
        // 1) Ensure Tone context is usable
        await Tone.start();
        let raw = Tone.getContext().rawContext as AudioContext;

        // iOS can end up "closed" or "interrupted"/"suspended"
        if (raw.state === "closed") {
          const newCtx = new Tone.Context({ latencyHint: "interactive" });
          Tone.setContext(newCtx);
          await Tone.start();
          raw = Tone.getContext().rawContext as AudioContext;
        }

        // Try resume; if it fails, gesture is missing (caller must be a gesture)
        if (raw.state !== "running") {
          try { await raw.resume(); } catch {}
        }

        // 2) Decide if we MUST rebuild sampler
        const currentRaw = Tone.getContext().rawContext as AudioContext;
        const existing = samplerRef.current as any;
        const samplerRaw = existing?.context?.rawContext as AudioContext | undefined;

        const ctxMismatch = samplerRaw && samplerRaw !== currentRaw;
        const mustRebuild = forceRebuild || ctxMismatch;

        if (mustRebuild) {
          try { samplerRef.current.dispose(); } catch {}
          samplesLoadedRef.current = false;

          const next = new Tone.Sampler({ urls: {}, release: 1 }).toDestination();
          samplerRef.current = next;
          setSampler(next);
          (window as any).__museflowSampler = next;
        } else {
          (window as any).__museflowSampler = samplerRef.current;
        }

        // 3) Load samples into *the current sampler* (decode must use currentRaw)
        if (!samplesLoadedRef.current) {
          const s = samplerRef.current;
          for (const [note, b64] of Object.entries(SALAMANDER_B64)) {
            const buf = await decodeBase64ToAudioBuffer(currentRaw as any, b64 as any);
            s.add(note as any, buf);
          }
          samplesLoadedRef.current = true;
        }

        // 4) Broadcast readiness
        (window as any).__audioUnlocked = true;
        window.dispatchEvent(new CustomEvent("AudioReady", { detail: { ready: true } }));
      })();

      try {
        await loadingRef.current;
      } finally {
        loadingRef.current = null;
      }
    };

    return () => {
      delete (window as any).__ensureSamplerReady;
    };
  }, []);

  // const sampler = React.useMemo(() => {
  //   return IS_NATIVE
  //     ? new Tone.Sampler({ urls: {}, release: 1 }).toDestination() // native: we will .add() buffers
  //     : new Tone.Sampler(TONE_SAMPLES).toDestination();            // web: URL-based samples
  // }, []);


  // React.useEffect(() => {
  //   samplerRef.current = new Tone.Sampler({ urls: {}, release: 1 }).toDestination();
  //   return () => {
  //     samplerRef.current?.dispose?.();
  //     samplerRef.current = null;
  //   };
  // }, []);
  
  // React.useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       await Tone.start(); // unlock context once
  //       const ctx = Tone.getContext().rawContext;
  //       // decode and add all notes
  //       for (const [note, b64] of Object.entries(SALAMANDER_B64)) {
  //         // fixme
  //         const buf = await decodeBase64ToAudioBuffer(ctx as any, b64);
  //         sampler.add(note as any, buf); // <-- no fetch/XHR involved
  //       }
  //       await sampler.loaded;
  //       // if (!cancelled) setSamplerReady(true);
  //     } catch (e) {
  //       console.error('Sampler load failed', e);
  //     }
  //   })();
  //   return () => { cancelled = true; };
  // }, [sampler]);

  const [initialized, setInitialized] = useState(false);
  const [screenOrientationSet, setScreenOrientationSet] = useState(false)

  const [renderRepertoirePlay, setRenderRepertoirePlay] = useState(true); // This will switch false & true to re-redner components to "refresh"

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer)
  const { sessionId } = useSelector((state: EventReducer) => state.eventReducer)
  const [playToneSound, SetplayToneSound] = useState(true);

  function getOperatingSystemBackup() {
    let operatingSystem = "Not known";
    if (window.navigator.appVersion.indexOf("Win") !== -1) {
      operatingSystem = "Windows OS";
    }
    if (window.navigator.appVersion.indexOf("Mac") !== -1) {
      operatingSystem = "MacOS";
    }
    if (window.navigator.appVersion.indexOf("X11") !== -1) {
      operatingSystem = "UNIX OS";
    }
    if (window.navigator.appVersion.indexOf("Linux") !== -1) {
      operatingSystem = "Linux OS";
    }

    return operatingSystem;
  }

useEffect(() => {
  CapApp.addListener('appUrlOpen', async ({ url }) => {
    if (!url?.startsWith('museflow://auth-callback')) return;
    try {
      navigate('/roadmap');
      await oidcManager.signinRedirectCallback(url);
      // tokens are now stored, auth.isAuthenticated becomes true
      try { await Browser.close(); } catch {}
    } catch (err) {
      console.error('OIDC redirect handling failed:', err);
    }
  });
}, [oidcManager]);


  useEffect(() => {
    const autoplayClickHandler = () => dispatch(appActions.setInteractedWithPage(true));
    window.addEventListener("mousedown", autoplayClickHandler);
    return () => {
      window.removeEventListener("mousedown", autoplayClickHandler);
    }
  }, [dispatch]);

  useEffect(() => {
    if ( auth.isAuthenticated && location.pathname.includes('signin') ) {
      navigate('/roadmap');
    }
  },[auth.user, auth.isAuthenticated, location.pathname]);

  useEffect(() => {
    if (
      location.pathname == '/tutorial' ||
      location.pathname == '/lesson' ||
      location.pathname == '/roadmap'
    ) {
      dispatch(lessonActions.setTierSummaryRequestError(false))
      dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
      dispatch(lessonActions.setTierSummary(null))
      dispatch(lessonActions.setLessonProcessPending(false))
    }
  }, [location.pathname])
  const dispatchInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopDispatchInterval = React.useCallback(() => {
    if (dispatchInterval.current) {
      clearInterval(dispatchInterval.current);
      dispatchInterval.current = null;
    }
  }, []);

  const startDispatchInterval = React.useCallback(() => {
    stopDispatchInterval();
    dispatchInterval.current = setInterval(() => {
      // Use the singleton directly to avoid silent saga failures when timers are throttled.
      eventQueue.dispatchEvents().catch((err) =>
        logger.warn("event dispatch interval failed", err),
      );
      const dispatchEvents = (eventActions as any).dispatchEvents;
      if (typeof dispatchEvents === 'function') {
        dispatch(dispatchEvents());
      }
    }, eventIntervalSecondsInt * 1000);
  }, [dispatch, eventIntervalSecondsInt, stopDispatchInterval]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      stopDispatchInterval();
      return;
    }

    startDispatchInterval();

    let appStateSub: PluginListenerHandle | undefined;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        startDispatchInterval();
        eventQueue.dispatchEvents().catch(() => {});
      } else {
        stopDispatchInterval();
        eventQueue.dispatchEvents().catch(() => {});
      }
    }).then(handle => { appStateSub = handle; }).catch(() => {});

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopDispatchInterval();
      } else {
        startDispatchInterval();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopDispatchInterval();
      appStateSub?.remove?.();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [auth.isAuthenticated, startDispatchInterval, stopDispatchInterval]);

  useEffect(() => {
    if (auth.isAuthenticated && data.userData) {
      // only call once userData row has been created & is present
      dispatch(appActions.getSubscriptionStatus());
    }
    if (auth.isAuthenticated && !initialized) {
      logger.info(`
 __  __                    _____  _                 
|  \\/  | _   _  ___   ___ |  ___|| |  ___ __      __
| |\\/| || | | |/ __| / _ \\| |_   | | / _ \\ \\ /\\ / /
| |  | || |_| |\\__ \\|  __/|  _|  | || (_) |\\ V  V / 
|_|  |_| \\__,_||___/ \\___||_|    |_| \\___/  \\_/\\_/  
      `);
      dispatch(appActions.authenticatedStartup());
    }
  }, [auth.isAuthenticated, initialized]);

  useEffect(() => {
    if (auth.user && sessionId && auth?.user?.attributes?.sub) {
      console.log("adding session attributes")
      awsRum?.addSessionAttributes({
        museflowUserId: auth?.user?.attributes?.sub,
        analyticsSessionId: sessionId,
        // Add device/browser info for easier filtering (also in metadata by default)
        userBrowser: browser?.name || 'unknown',
        userBrowserVersion: browser?.version || 'unknown',
        userOS: browser?.os || 'unknown',
        userDeviceType: detectDevice(),
      });
    }

    if (auth.user && sessionId && !initialized) {
      dispatch(eventActions.visitEventAction());
      // we only want this to fire when the page loads, but we need to wait
      // until the user data is loaded and the session id is set.
      setInitialized(true);
    }
  }, [auth.user, sessionId, initialized, data.userData?.user_id]);

  useEffect(() => {
    let previousLocation = sessionStorage.getItem("previousLocation");
    // in case of refresh; don't set unless changed
    if (previousLocation != location.pathname) {
      sessionStorage.setItem('previousLocation', location.pathname)
    }

    if (process.env.REACT_APP_NODE_ENV == "prd") {
      const gtag_tracking_id = "AW-11476609062";
      // Initialize Google Tracking Tag / Analytics (only once)
      if (!window.dataLayer) {
        window.dataLayer = [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
        window.gtag("config", gtag_tracking_id);
      }
      // Track page views:
      const pagePath = location.pathname + location.search;
      window.gtag("config", gtag_tracking_id, { page_path: pagePath });
      awsRum?.recordPageView(location.pathname);
      ReactGA?.send({ hitType: "pageview", page: location.pathname });
    }
  }, [location]);


  useEffect(() => {
    if (!data.userData || !currentAuthToken) return;
      // At this point, you can call your setup function or directly use window.store.
      try {
        // Example: call your custom IAP setup function
        setupIAP(data.userData, dispatch, currentAuthToken);
      } catch (err) {
        console.error("IAP init failed:", err);
      }
  }, [data.userData, dispatch, currentAuthToken]);


  useEffect(() => {
    dispatch(appActions.setInteractedWithPage(false));
  }, []);

  
  useEffect(() => {
    if (!isCapacitorNative()) {
      setScreenOrientationSet(true)
      return;
    }
    const forceLandscape = LANDSCAPE_PATHS.some(p =>
      location.pathname.startsWith(p)
    );
    (async () => {
      try {
        if (forceLandscape) {
          await ScreenOrientation.lock({ orientation: 'landscape' });
          setTimeout(() => { 
            setScreenOrientationSet(true)
          }, 150)
        } else {
          await ScreenOrientation.unlock();
          setTimeout(() => { 
            setScreenOrientationSet(true)
          }, 150)
        }
      } catch (err) {
        console.warn('Orientation lock error', err);
      }
    })();
  }, [location.pathname]);



  // Using "userData" to check if the state data has loaded and persisted.
  // Without this check; tutorial page and lesson page will error before data is loaded
  // Note that we check "user" well; user may need to go to sign in/up.
  if ((auth.isAuthenticated && !data.userData)) {
    console.warn(
      "User data not loaded yet, showing loading screen.",
      "auth.isLoading: ",auth.isLoading,
      "auth.isAuthenticated: ",auth.isAuthenticated,
      "data.userData: ",data.userData,
      "auth.oauthPending: ",auth.oauthPending
    );
    return (
      <Box>
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  

  return (
    <MidiProvider location={location} navigate={navigate} sampler={sampler}>     
      <Routes>
        <Route path="/register" element={<Signup />} />
        <Route path="/signin" element={<Signin />} />
        <Route path="/verify" element={<Verification />} />
        {/* <Route path='/forgot' element={<Forgot/>} /> */}
        {/*  Make sure to use these two (looks pretty good already) */}
        <Route
          path="/plan"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Plan />{" "}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/payment' element={<ProtectedRoute redirectPath='/signin'> <Payment/> </ProtectedRoute>} /> */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Welcome />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/sound-output"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <SoundOutput />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/roadmap"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Roadmap />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/repertoire"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Repertoire />{" "}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/home' element={<ProtectedRoute redirectPath='/signin'> <Home/> </ProtectedRoute>} /> */}
        {/* <Route path='/browse' element={<ProtectedRoute redirectPath='/signin'> <Browse/> </ProtectedRoute>} /> */}
        <Route
          path="/repertoire-play"
          element={
            <ProtectedRoute redirectPath="/signin">
              {renderRepertoirePlay && (
                <RepertoirePlayProvider>
                  <RepertoirePlay
                    renderRepertoirePlay={renderRepertoirePlay}
                    setRenderRepertoirePlay={setRenderRepertoirePlay}
                  />
                </RepertoirePlayProvider>
              )}
            </ProtectedRoute>
          }
        />
        {/* Still allowing the FAQ route, but will redirect to https://museflow.ai/faq */}
        <Route
          path="/faq"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Help />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Settings />{" "}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/leveldesscription' element={<ProtectedRoute redirectPath='/signin'> <LevelDescription/> </ProtectedRoute>} /> */}
        <Route
          path="/exercises/note-length"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <NoteLength
                notes={[
                  NoteType.QUARTER,
                  NoteType.QUARTER,
                  NoteType.QUARTER,
                  NoteType.QUARTER,
                ]}
                bpm={72}
              />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutorial"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Tutorial />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/introduction"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <Introduction />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/trial-welcome"
          element={
            <ProtectedRoute redirectPath="/signin">
              {" "}
              <TrialWelcome />{" "}
            </ProtectedRoute>
          }
        />
        <Route
          path="/lesson"
          element={
            <ProtectedRoute redirectPath="/signin">
              <Lesson />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute redirectPath="/signin">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/404" element={<ErrorPage />} />
        <Route path="/" element={<Navigate to="/roadmap" replace />} />
        {/* Navigate homepage to roadmap */}
        <Route path="/*" element={<Navigate to="/404" />} />
      </Routes>
    </MidiProvider>
  );
};

//const RootApp: React.FC<AppProps> = ({ store }) => {
const RootApp: React.FC<any> = () => {
  return (
    <div className="App">
        <AuthWrapperProvider>
          <BrowserRouter>
            <AudioUnlocker />
            <OfflineModal />
            <DeviceCompatibilityBlockingScreen/>
            {
              process.env.REACT_APP_MAINTENANCE == "true" &&
              <MaintenancePage />
            }
            {
              process.env.REACT_APP_MAINTENANCE != "true" &&
              <App />
            }
          
          </BrowserRouter>
        </AuthWrapperProvider>
    </div>
  );
};

interface ErrorBoundaryProps {
  //store: Store<RootState>;
  store: Store<any>;
}

// class ErrorBoundary extends React.Component<{}, {hasError: boolean}> {
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {

  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }
  componentDidCatch(error: Error, errorInfo: any) {
    // You can also log the error to an error reporting service
    awsRum?.recordError(error);
  }
  render() {
    // No need to present error page on every uncaught error
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <ErrorPage />
      );
    } else {
      return (
       <RootApp store={this.props.store} />
      );
    }
  }
}

export default RootApp;